import React from 'react';
import { css } from '@emotion/react';

import { hasPermission } from '$/utils/hasPermission';
import { formatCurrency } from '$/utils/formatCurrency';
import { rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';

import { Portal } from '$/components/Portal';
import SimpleModal from '$/components/ui/SimpleModal';
import { ErrorMessage } from '$/components/ui/MessageText';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';

import { Per } from './Components';
import { usePlanPageContext } from '../PlanPage';
import { useUpdateAddOn } from './useUpdateAddOn';

import type { AdditionalRegistrants } from './Info/AdditionalRegistrants';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { useMe } from '$/hooks/useMe';
import isMobile from '$/utils/isMobile';
import EDate from '$/shared/frontend/utils/EDate';
import Button from '$/components/ui/Button';
import { SubscriptionSubstatus } from '$/GraphQL/schema/types';

const formatThousandsToK = (value: number): string => {
  return `${value / 1000}${value == 0 ? '' : 'K'}`;
};

const UpdateRegistrantsModal: React.FC<{
  closeModal: () => void;
  addOn: AdditionalRegistrants;
  quantity: number;
}> = ({ closeModal, addOn, quantity }) => {
  const me = useMe();
  const etheme = useThemeContext();
  const { subscriptionInfo } = usePlanPageContext();
  const { submitting, updateAddOn, error } = useUpdateAddOn({
    onSuccess: closeModal,
  });

  const canUpdateAddon = hasPermission(rolesPermissionsMatrix.accountSubscription.path, me);
  const onSubmit = React.useCallback(async (): Promise<void> => {
    if (submitting) return;
    await updateAddOn({
      type: addOn.type,
      quantity: quantity,
    });
  }, [addOn.type, quantity, submitting, updateAddOn]);

  const isIncrease = quantity > addOn.quantity;

  const title = isIncrease
    ? 'Increase additional registrants per month?'
    : 'Decrease additional registrants per month?';
  const buttonText = isIncrease
    ? 'Increase additional registrants'
    : 'Decrease additional registrants';
  const from = addOn.scheduledQuantity;
  const to = quantity;
  const registrantLimitFrom = subscriptionInfo.product.registrantLimit + from;
  const registrantLimitTo = subscriptionInfo.product.registrantLimit + to;
  const priceFrom = addOn.calculatePrice(subscriptionInfo.billingCycle, from);
  const priceTo = addOn.calculatePrice(subscriptionInfo.billingCycle, to);
  const nextPaymentDueDate = subscriptionInfo.nextPaymentDueDate;
  const per = <Per billingCycle={subscriptionInfo.billingCycle} />;

  const modalMessages = isIncrease ? (
    <>
      <p>
        If you change the number of additional registrants tacked on to your subscription from{' '}
        {formatThousandsToK(from)} to {formatThousandsToK(to)}, the amount you pay for additional
        registrants will go up from {formatCurrency(priceFrom)}
        {per} to {formatCurrency(priceTo)}
        {per} and your <b>Monthly registrant limit</b> will be increased from{' '}
        {formatThousandsToK(registrantLimitFrom)} to {formatThousandsToK(registrantLimitTo)}.
      </p>
      <p>
        You will be immediately charged this full amount and given all{' '}
        {formatThousandsToK(to - from)} additional registrants now, regardless of the time left
        before your monthly registrants reset on{' '}
        {new EDate(me.team.registrantLimit.resetAt).format('P')}.
      </p>
      <p className='font-semibold'>
        Once charged, additional registrants are non-refundable for the billing cycle in question.
        You may change the number of additional registrants associated with your subscription at any
        time.
      </p>
      <p className='mb-1e'>Do you wish to continue?</p>
    </>
  ) : (
    <>
      <p>
        If you change the number of additional registrants tacked on to your subscription from{' '}
        {formatThousandsToK(from)} to {formatThousandsToK(to)}, the amount you pay for additional
        registrants will go down from {formatCurrency(priceFrom)}
        {per} to {formatCurrency(priceTo)}
        {per}. However, this change will not go into effect until your next billing date, on{' '}
        {nextPaymentDueDate?.format('P')}.
      </p>
      <p>
        At that time, your <b>Monthly registrant limit</b> will be decreased from{' '}
        {formatThousandsToK(registrantLimitFrom)} to {formatThousandsToK(registrantLimitTo)}.
      </p>
      <p>
        You may change the number of additional registrants associated with your subscription at any
        time.
      </p>
      <p className='mb-1e'>Do you wish to continue?</p>
    </>
  );
  return (
    <SimpleModal
      closeOnClickOutside={false}
      title={title}
      className='md:min-w-40e'
      onCancel={closeModal}
      cancel={<Button className='mr-4'>Cancel</Button>}
      submit={
        <Button.submit
          theme={etheme.controls.button.action}
          disabled={!canUpdateAddon}
          css={css`
            font-weight: 400;
          `}
          onClick={(): void => void onSubmit()}
          submitting={submitting}
        >
          <span className='flex px-1/4e'>{buttonText}</span>
        </Button.submit>
      }
    >
      {modalMessages}
      {!canUpdateAddon && (
        <p className='text-color-error mb-2'>You need to be an admin to access this function.</p>
      )}
      {error && (
        <p className='text-color-error mb-2'>
          {error} If this problem persists, please contact{' '}
          <a href='mailto://support@ewebinar.com'>support@ewebinar.com</a>.
        </p>
      )}
    </SimpleModal>
  );
};

export const getListComponent = (addOn: AdditionalRegistrants): React.FC => {
  const ListComponent: React.FC = () => {
    const [showModal, setShowModal] = React.useState(false);
    const { error, submitting, updateAddOn } = useUpdateAddOn();
    const { subscriptionInfo, subscriptionChanges, onSubscriptionChange } = usePlanPageContext();

    const quantity = subscriptionChanges[addOn.type] ?? addOn.scheduledQuantity;
    const noOfPackage = Math.ceil(quantity / 1000);

    const onCancel = React.useCallback(() => {
      onSubscriptionChange(addOn.type, addOn.scheduledQuantity);
    }, [onSubscriptionChange]);

    const closeModal = React.useCallback(() => {
      setShowModal(false);
    }, []);

    const onButtonClick = React.useCallback(() => {
      if (
        !subscriptionInfo.isSuspended &&
        (quantity > addOn.quantity || quantity < addOn.scheduledQuantity)
      ) {
        setShowModal(true);
        return;
      }

      updateAddOn({
        type: addOn.type,
        quantity,
      });
    }, [quantity, subscriptionInfo.isSuspended, updateAddOn]);

    const buttons = React.useMemo(() => {
      if (subscriptionInfo.subStatus === SubscriptionSubstatus.Pending) return null;
      if (quantity === addOn.scheduledQuantity) return null;
      let colSpan = 2;
      if (subscriptionInfo.isLegacyTeam && !isMobile) colSpan = 1;
      return (
        <Portal containerId='current-plan-buttons'>
          <td className='text-right align-middle pt-3 hidden md:table-cell'>
            <Button.blueText onClick={onCancel}>Cancel</Button.blueText>
          </td>
          <td className='bg-transparent align-middle pt-3 opacity-100 table-cell' colSpan={colSpan}>
            {error && <ErrorMessage error={error} />}
            <div className='flex'>
              <Button.blueText className='md:hidden mr-2' onClick={onCancel}>
                Cancel
              </Button.blueText>
              <Button.submit submitting={submitting} onClick={onButtonClick} className='w-full'>
                Update additional registrants
              </Button.submit>
            </div>
          </td>
        </Portal>
      );
    }, [
      error,
      onButtonClick,
      onCancel,
      quantity,
      submitting,
      subscriptionInfo.isLegacyTeam,
      subscriptionInfo.subStatus,
    ]);

    if (subscriptionInfo.isInTrial) return null;
    return (
      <>
        <tr>
          <td>
            <div>
              {formatThousandsToK(subscriptionInfo.nextPriceTier.registrantLimit)} registrants/month
              included
            </div>
          </td>
          {[addOn.priceTier?.monthlyPrice, addOn.priceTier?.annualPricePerMonth].map((price) => {
            return <td className='price' key={price} />;
          })}
        </tr>
        <tr>
          <td
            css={css`
              .ui.selection.dropdown {
                padding: 0.5rem 0.75rem;
                min-height: auto;
              }
              .ui.selection.dropdown > .dropdown.icon {
                padding: 0.5rem;
              }
              .ui.selection.dropdown .menu > .item,
              .ui.dropdown .menu > .item {
                padding: 0.5rem 0.75rem !important;
              }
              @media screen and (min-width: 992px) {
                .ui.selection.dropdown .menu {
                  max-height: 13rem !important;
                }
              }
            `}
          >
            <div>
              <Dropdown
                className='w-5e text-right pr-2e inline-block align-top mr-1'
                upward
                value={quantity}
                options={[
                  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30,
                  35, 40, 45, 50, 60, 70, 80, 90, 100,
                ]
                  .filter((value) => {
                    const min = subscriptionInfo.isCancelled ? addOn.quantity : 0;
                    return value * 1000 >= min;
                  })
                  .map((value) => ({
                    value: value * 1000,
                    text: formatThousandsToK(value * 1000),
                  }))}
                onChange={(value): void => onSubscriptionChange(addOn.type, value)}
              />{' '}
              <div className='inline-block leading-5'>additional registrants/month</div>
            </div>
            {addOn.quantity !== addOn.scheduledQuantity && (
              <div className='italic text-sm text-coral-1 leading-4 mt-2'>
                You will continue to have {formatThousandsToK(addOn.quantity)} additional
                registrants/month until {subscriptionInfo.nextPaymentDueDate?.format('P')}
              </div>
            )}
            {showModal && (
              <UpdateRegistrantsModal addOn={addOn} quantity={quantity} closeModal={closeModal} />
            )}
          </td>
          {[addOn.priceTier?.monthlyPrice, addOn.priceTier?.annualPricePerMonth].map((price) => {
            return (
              <td className='price' key={price}>
                <div>
                  {formatCurrency(noOfPackage * (price ?? 0))}
                  <Per />
                </div>
                <div className='text-sm text-gray-1 italic leading-1e'>
                  @ {formatCurrency(price ?? 0)} per 1,000
                </div>
              </td>
            );
          })}
        </tr>
        {buttons}
      </>
    );
  };
  return ListComponent;
};
